import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const FLAT_SHIPPING = 10.0;
  const FREE_SHIP_QTY = 2;
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const freeShipping = totalQty >= FREE_SHIP_QTY;
  const shippingCost = freeShipping ? 0 : FLAT_SHIPPING;
  const total = subtotal + shippingCost;

  useEffect(() => {
    if (items.length === 0) navigate("/shop");
  }, [items, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, variant: i.variant, quantity: i.quantity })),
        email,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/create", payload);
      clear();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-[1100px] mx-auto px-6 md:px-10 py-12 md:py-20">
      <div className="label-eyebrow mb-3">Checkout · Guest</div>
      <h1 className="font-serif-display text-4xl md:text-6xl leading-none mb-2">Almost there.</h1>
      <p className="text-sm opacity-60 mb-10 md:mb-12 max-w-xl">
        No account needed. Enter your email — you'll pick your shipping address and pay securely on Stripe.
      </p>

      <form onSubmit={submit} className="grid md:grid-cols-12 gap-10 md:gap-12">
        <div className="md:col-span-7 space-y-8">
          <section>
            <h2 className="font-serif-display text-2xl mb-2">Contact</h2>
            <p className="text-xs opacity-60 mb-4">We'll send your order confirmation and tracking here.</p>
            <input
              data-testid="ck-email"
              required
              type="email"
              className="kaiya-input w-full"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              autoFocus
            />
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-3">Shipping</h2>
            <div
              className="flex items-center justify-between gap-4 p-5 rounded-2xl border"
              style={{ borderColor: "var(--kaiya-primary)", background: "var(--kaiya-bg-2)" }}
              data-testid="ck-flat-shipping"
            >
              <div>
                <div className="font-medium flex items-center gap-2">
                  {freeShipping ? "Free Shipping" : "Standard Shipping"}
                  {freeShipping && (
                    <span
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: "var(--kaiya-primary)", color: "white" }}
                    >
                      Unlocked
                    </span>
                  )}
                </div>
                <div className="text-xs opacity-60 mt-0.5">
                  5-9 business days · flat rate across Canada · free when you buy 2 or more
                </div>
              </div>
              <div className="font-serif-display text-xl">
                {freeShipping ? (
                  <span style={{ color: "var(--kaiya-primary)" }}>Free</span>
                ) : (
                  <>${FLAT_SHIPPING.toFixed(2)}</>
                )}
              </div>
            </div>
            {!freeShipping && (
              <p
                className="mt-3 text-sm"
                data-testid="ck-free-ship-nudge"
                style={{ color: "var(--kaiya-primary)" }}
              >
                Add {FREE_SHIP_QTY - totalQty} more towel{FREE_SHIP_QTY - totalQty === 1 ? "" : "s"} and shipping is on us.
              </p>
            )}
          </section>

          <section>
            <button
              data-testid="ck-submit"
              disabled={submitting || !email}
              type="submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
              Continue to secure checkout — ${total.toFixed(2)}
            </button>
            <p className="mt-4 text-xs opacity-60 text-center max-w-md mx-auto">
              You'll enter your shipping address and payment details on Stripe's secure page.
              We accept Visa, Mastercard, Amex, and Apple/Google Pay.
            </p>
          </section>
        </div>

        {/* Order summary */}
        <aside className="md:col-span-5">
          <div className="kaiya-card p-6 md:p-8" style={{ background: "var(--kaiya-bg-2)" }}>
            <div className="label-eyebrow mb-4">Your order</div>
            <div className="space-y-5 mb-6">
              {items.map((it, idx) => (
                <div key={`${it.product_id}-${it.variant || ""}`} className="flex gap-4">
                  <div className="image-soft w-16 h-20 shrink-0">
                    <img src={it.image} className="w-full h-full object-cover" alt={it.name} />
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{it.name}</div>
                    {it.variant && <div className="opacity-60 text-xs mt-0.5">{it.variant}</div>}
                    <div className="opacity-70 text-xs mt-1">Qty {it.quantity}</div>
                  </div>
                  <div className="font-serif-display text-base">${(it.unit_price * it.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-2 text-sm" style={{ borderColor: "var(--kaiya-border)" }}>
              <div className="flex justify-between">
                <span className="opacity-70">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-70">Shipping</span>
                <span>
                  {freeShipping ? (
                    <span style={{ color: "var(--kaiya-primary)" }}>Free</span>
                  ) : (
                    `$${FLAT_SHIPPING.toFixed(2)}`
                  )}
                </span>
              </div>
              <div
                className="flex justify-between pt-3 border-t mt-3 font-serif-display text-2xl"
                style={{ borderColor: "var(--kaiya-border)" }}
              >
                <span>Total</span>
                <span data-testid="ck-total">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}
